import {
  startTransition,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { View } from 'react-native'
import { useFocusEffect } from 'expo-router'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'
import { useReanimatedKeyboardAnimation } from 'react-native-keyboard-controller'
import Segmented from '@ca/ui/elements/Segmented'
import { SegmentedOption } from '@ca/ui/elements/Segmented/types'
import { CameraStatusOverlay } from './CameraStatusOverlay'
import { HSATab } from './HSATab'
import { LiveTab } from './LiveTab'
import { MeasureTab } from './MeasureTab'
import { SHTab } from './SHTab'
import { SettingsModal } from './SettingsModal'
import { TABS, TabId } from './constants'
import { useHSACapture } from './useHSACapture'
import { usePdMeasurement } from './usePdMeasurement'
import { useSHCapture } from './useSHCapture'
import { LIVE_FREEZE_STATUS, useFaceDataStore } from './faceDataStore'
import {
  ExpoOpticalMeasureView,
  getOpticalMeasureSettings,
  resolveOpticalMeasureFramingSettings,
} from '../index'
import type {
  FaceData,
  FaceDataEvent,
  OpticalMeasureFreezeCommandResult,
  OpticalMeasurePointSide,
  OpticalMeasureSettings,
  OpticalMeasureViewHandle,
} from '../index'

const PANEL_RATIO = 1 / 3
const MIN_PANEL_HEIGHT = 450
const FRAME_THROTTLE_MS = 50
const MODE_BY_TAB = {
  live: 'pd',
  pd: 'pd',
  sh: 'sh',
  hsa: 'hsa',
} as const satisfies Record<TabId, 'pd' | 'sh' | 'hsa'>

type SettingsModalScope = Extract<TabId, 'pd' | 'sh' | 'hsa'>

const SETTINGS_MODAL_PATHS = {
  pd: [
    'input.pupilDetection',
    'input.visionOrientation',
    'framing',
    'geometry.corneaProjectionOffset',
    'draw.debugShapesEnabled',
  ],
  sh: ['sh', 'framing', 'draw', 'input'],
  hsa: ['hsa', 'draw.debugShapesEnabled'],
} as const satisfies Record<SettingsModalScope, readonly string[]>

export type OpticalMeasureDevScreenProps = {
  isDev?: boolean
  isDebug?: boolean
}

export function OpticalMeasureDevScreen({
  isDev = false,
  isDebug = false,
}: OpticalMeasureDevScreenProps) {
  const tabs = TABS.filter((option) => isDev || option.id !== 'hsa')
  const [tab, setTab] = useState<SegmentedOption<TabId>>(TABS[0])
  const [settingsModalScope, setSettingsModalScope] =
    useState<SettingsModalScope | null>(null)
  const [settings, setSettings] = useState<OpticalMeasureSettings>(() => {
    const base = getOpticalMeasureSettings(isDev || isDebug)
    // The dev screen has no floating bottom overlays, so it doesn't need the
    // production vertical offset that keeps the zoomed ROI clear of them.
    return {
      ...base,
      sh: {
        ...base.sh,
        review: { ...base.sh.review, verticalOffsetFactor: 0 },
      },
    }
  })
  const [containerHeight, setContainerHeight] = useState(0)
  const [cameraReady, setCameraReady] = useState(false)
  const lastFrameRef = useRef(0)
  const measureViewRef = useRef<OpticalMeasureViewHandle>(null)
  const lastPdAutoFreezeResultRef = useRef<unknown>(null)
  const setFaceData = useFaceDataStore((s) => s.setData)
  const setFreezeStatus = useFaceDataStore((s) => s.setFreezeStatus)

  useFocusEffect(
    useCallback(() => {
      // Mount the camera after a painted frame (see useCameraMountAfterPaint);
      // the double rAF spans the first paint so it never opens on black.
      let frame = requestAnimationFrame(() => {
        frame = requestAnimationFrame(() => setCameraReady(true))
      })
      return () => {
        cancelAnimationFrame(frame)
        setCameraReady(false)
      }
    }, [])
  )

  const { height: keyboardHeight } = useReanimatedKeyboardAnimation()

  const [calibrationKey, setCalibrationKey] = useState(0)

  const framingProfile =
    settings.sh.scale.vboxMm > 0
      ? settings.framing.vbox
      : settings.framing.normal
  const pd = usePdMeasurement(settings.framing.normal)
  const sh = useSHCapture(settings.sh.capture, framingProfile)
  const hsa = useHSACapture()

  const updateFreezeStatus = (
    result: OpticalMeasureFreezeCommandResult | undefined
  ) => {
    if (result?.freeze) setFreezeStatus(result.freeze)
  }

  const freezeCurrentFrame = async () => {
    updateFreezeStatus(await measureViewRef.current?.freezeCurrentFrame?.())
  }

  const setLatestFreeze = async () => {
    const result = await measureViewRef.current?.setLatestFreeze?.()
    updateFreezeStatus(result)
    return result
  }

  const clearNativeFreeze = async () => {
    updateFreezeStatus(await measureViewRef.current?.clearFreeze?.())
  }

  const setRejectedFinalPreview = async (enabled: boolean) => {
    const result =
      await measureViewRef.current?.setRejectedFinalPreview?.(enabled)
    updateFreezeStatus(result)
    return result?.ok === true
  }

  const offsetSHBottomPoint = async (
    side: OpticalMeasurePointSide,
    amount: number
  ) => {
    const result = await measureViewRef.current?.offsetPoint?.({
      type: 'sh-bottom',
      side,
      unit: 'mm',
      amount,
    })
    updateFreezeStatus(result)
    return result?.ok === true
  }

  const resetSHBottomPoint = async (side?: OpticalMeasurePointSide) => {
    const result = await measureViewRef.current?.resetPoint?.({
      type: 'sh-bottom',
      side,
    })
    updateFreezeStatus(result)
    return result?.ok === true
  }

  const rerunHSAFreeze = async () => {
    updateFreezeStatus(await measureViewRef.current?.rerunHSAFreeze?.())
  }

  const startNativeSHCapture = async () => {
    const result = await measureViewRef.current?.startSHCapture?.()
    sh.applyNativeState(result?.shCapture)
  }

  const cancelNativeSHCapture = async () => {
    const result = await measureViewRef.current?.cancelSHCapture?.()
    sh.applyNativeState(result?.shCapture)
  }

  const clearNativeSHCapture = async () => {
    const result = await measureViewRef.current?.clearSHCapture?.()
    sh.applyNativeState(result?.shCapture)
  }

  const startNativeHSACapture = async () => {
    const result = await measureViewRef.current?.startHSACapture?.()
    hsa.applyNativeState(result?.hsaCapture)
  }

  const cancelNativeHSACapture = async () => {
    const result = await measureViewRef.current?.cancelHSACapture?.()
    hsa.applyNativeState(result?.hsaCapture)
  }

  const clearNativeHSACapture = async () => {
    const result = await measureViewRef.current?.clearHSACapture?.()
    hsa.applyNativeState(result?.hsaCapture)
  }

  const recalibrate = () => {
    setCalibrationKey((k) => k + 1)
  }
  const startPD = () => {
    void clearNativeFreeze()
    pd.start()
  }

  const startSHWithCalibration = async () => {
    await clearNativeFreeze()
    recalibrate()
    sh.start()
    await startNativeSHCapture()
  }

  const startHSA = async () => {
    await clearNativeFreeze()
    hsa.start()
    await startNativeHSACapture()
  }

  const setShVboxMm = (vboxMm: number) => {
    setSettings((prev) => ({
      ...prev,
      sh: {
        ...prev.sh,
        scale: {
          ...prev.sh.scale,
          vboxMm,
        },
      },
    }))
  }

  useEffect(() => {
    if (pd.phase !== 'complete' || !pd.result) {
      lastPdAutoFreezeResultRef.current = null
      return
    }
    if (lastPdAutoFreezeResultRef.current === pd.result) return

    lastPdAutoFreezeResultRef.current = pd.result
    void freezeCurrentFrame()
  }, [pd.phase, pd.result, freezeCurrentFrame])

  const activeTab = tab
  const mode = MODE_BY_TAB[activeTab.id]
  const phaseByTab = {
    live: null,
    pd: pd.phase,
    sh: sh.phase,
    hsa: hsa.phase,
  }
  const progressByTab = {
    live: 0,
    pd: pd.progress,
    sh: sh.progress,
    hsa: hsa.progress,
  }
  const activePhase = phaseByTab[activeTab.id]
  const activeProgress = progressByTab[activeTab.id]

  const onTabChange = (newTab: SegmentedOption<TabId>) => {
    if (
      activeTab.id === 'hsa' &&
      newTab.id !== 'hsa'
    ) {
      hsa.stop()
      void cancelNativeHSACapture()
    }
    setTab(newTab)
  }

  const onFaceData = (incoming: FaceData) => {
    setFreezeStatus(incoming.freeze ?? LIVE_FREEZE_STATUS)

    if (activeTab.id === 'pd') pd.handleFaceData(incoming)
    if (activeTab.id === 'sh') sh.handleFaceData(incoming)
    if (activeTab.id === 'hsa') hsa.handleFaceData(incoming)

    const now = Date.now()
    const isFrozenUpdate = incoming.freeze?.active === true
    if (!isFrozenUpdate && now - lastFrameRef.current < FRAME_THROTTLE_MS)
      return
    lastFrameRef.current = now
    startTransition(() => setFaceData(incoming))
  }

  const cameraStyle = useAnimatedStyle(() => {
    const panelBase = Math.max(containerHeight * PANEL_RATIO, MIN_PANEL_HEIGHT)
    return { height: containerHeight - panelBase + keyboardHeight.value }
  })

  const panelStyle = useAnimatedStyle(() => {
    const panelBase = Math.max(containerHeight * PANEL_RATIO, MIN_PANEL_HEIGHT)
    return { height: panelBase - keyboardHeight.value }
  })

  return (
    <View
      className="flex-1"
      onLayout={(e) => setContainerHeight(e.nativeEvent.layout.height)}
    >
      <Animated.View style={cameraStyle}>
        {cameraReady && (
          <ExpoOpticalMeasureView
            style={{ flex: 1 }}
            ref={measureViewRef}
            mode={mode}
            settings={settings}
            calibrationKey={calibrationKey}
            contourDetectionActive={
              (mode === 'sh' && sh.phase !== 'idle' && sh.phase !== 'complete') ||
              (mode === 'hsa' && hsa.phase !== 'idle' && hsa.phase !== 'complete')
            }
            measuring={
              (mode === 'sh' &&
                sh.phase !== 'idle' &&
                sh.phase !== 'complete') ||
              (mode === 'hsa' &&
                hsa.phase !== 'idle' &&
                hsa.phase !== 'complete')
            }
            onFaceData={(e: FaceDataEvent) => onFaceData(e.nativeEvent)}
          />
        )}
        <CameraStatusOverlay
          activeTabId={activeTab.id}
          phase={activePhase}
          progress={activeProgress}
          containerHeight={containerHeight}
        />
      </Animated.View>

      <Animated.View className="overflow-hidden bg-white/95" style={panelStyle}>
        <View className="w-full py-2">
          <View className="self-center">
            <Segmented options={tabs} value={tab} onChange={onTabChange} />
          </View>
        </View>

        {tab.id === 'live' && <LiveTab />}
        {tab.id === 'pd' && (
          <MeasureTab
            phase={pd.phase}
            result={pd.result}
            onStart={startPD}
            onStop={pd.stop}
            onClear={() => {
              pd.clear()
              void clearNativeFreeze()
            }}
            onOpenSettings={() => setSettingsModalScope('pd')}
          />
        )}
        {tab.id === 'sh' && (
          <SHTab
            phase={sh.phase}
            capturedCount={sh.capturedCount}
            targetCount={sh.targetCount}
            progress={sh.progress}
            onStart={startSHWithCalibration}
            onStop={() => {
              sh.stop()
              void cancelNativeSHCapture()
            }}
            onClear={() => {
              sh.clear()
              void clearNativeSHCapture()
              void clearNativeFreeze()
            }}
            onRecalibrate={recalibrate}
            vboxMm={settings.sh.scale.vboxMm}
            onVboxMmChange={setShVboxMm}
            onSetRejectedFinalPreview={setRejectedFinalPreview}
            onOffsetPoint={offsetSHBottomPoint}
            onResetPoint={resetSHBottomPoint}
            onOpenSettings={() => setSettingsModalScope('sh')}
          />
        )}
        {tab.id === 'hsa' && (
          <HSATab
            phase={hsa.phase}
            result={hsa.result}
            debug={hsa.result?.debug ?? null}
            capturedCount={hsa.capturedCount}
            targetCount={hsa.targetCount}
            progress={hsa.progress}
            onStart={startHSA}
            onStop={() => {
              hsa.stop()
              void cancelNativeHSACapture()
            }}
            onClear={() => {
              hsa.clear()
              void clearNativeHSACapture()
              void clearNativeFreeze()
            }}
            onRerunFrozenFrame={rerunHSAFreeze}
            onOpenSettings={() => setSettingsModalScope('hsa')}
          />
        )}
      </Animated.View>

      <SettingsModal
        open={settingsModalScope != null}
        close={() => setSettingsModalScope(null)}
        settings={settings}
        paths={
          settingsModalScope
            ? SETTINGS_MODAL_PATHS[settingsModalScope]
            : SETTINGS_MODAL_PATHS.pd
        }
        sections={['frameFreeze']}
        isDev={isDev}
        isDebug={isDebug}
        onSave={(nextSettings) => {
          const framing =
            resolveOpticalMeasureFramingSettings(nextSettings.framing)
          setSettings({
            ...nextSettings,
            framing: framing ?? settings.framing,
          })
        }}
        onFreezeCurrentFrame={() => void freezeCurrentFrame()}
        onSetLatestFreeze={() => void setLatestFreeze()}
        onClearFreeze={() => {
          if (settingsModalScope === 'hsa') {
            hsa.clear()
            void clearNativeHSACapture()
          }
          void clearNativeFreeze()
        }}
      />
    </View>
  )
}
